import { useCallback, useEffect, useRef } from "react";

export type BulkAction =
  | "delete_selected"
  | "revoke_selected"
  | "export_selected"
  | "publish_selected"
  | "retry_selected"
  | "export_all";

export const SUCCESS_MESSAGES = {
  publish_selected: (name: string) => `${name} was published successfully!`,
  revoke_selected: (name: string) => `${name} was revoked successfully!`,
  delete_selected: (name: string) => `${name} was deleted successfully!`,
  retry_selected: (name: string) => `${name} was retried successfully!`,
  pause_selected: (name: string) => `${name} was paused successfully!`,
  resume_selected: (name: string) => `${name} was resumed successfully!`,
  terminate_selected: (name: string) => `${name} was terminated successfully!`,
  cancel_selected: (name: string) => `${name} was cancelled successfully!`,
  export_all: (name: string) =>
    `${name} download link will be sent to your email when it's ready!`,
  export_selected: () =>
    `${name} download link will be sent to your email when it's ready!`,
};

export const exportBulkActionNames = ["export_selected", "export_all"];

export const BULK_ACTION_NAMES = {
  delete_selected: "Delete",
  revoke_selected: "Revoke",
  export_selected: "Export",
  export_all: "Export",
  publish_selected: "Publish",
  retry_selected: "Retry",
  expire_selected: "Expire",
  close_selected: "Close",
  void_selected: "Void",
};

export function getBulkActionName(action: BulkAction) {
  return BULK_ACTION_NAMES[action] || action;
}

export const EXPORT_DROPDOWN_NAME = "export_dropdown";

/**
 * Used when you have a use case where a param key is repeated example: `?foo=bar&foo=rab&foo=123`.
 * Which cannot be handled in a regular JS object
 * @param {object} paramsObject
 * @returns {URLSearchParams}
 */
export const generateUrlParamsFromObject = (
  paramsObject: Record<string, string>
) => {
  const urlParams = new URLSearchParams();
  for (const [key, value] of Object.entries(paramsObject)) {
    if (!String(value)) continue;

    if (Array.isArray(value)) {
      value.forEach((entry) => urlParams.append(key, entry));
    } else {
      urlParams.append(key, value);
    }
  }

  return urlParams;
};

// skip react table internal state reset after data changes
export function useSkipper() {
  const shouldSkipRef = useRef(false);
  const shouldSkip = shouldSkipRef.current;

  // Wrap a function with this to skip a pagination reset temporarily
  const skip = useCallback(() => {
    shouldSkipRef.current = true;
  }, []);

  useEffect(() => {
    shouldSkipRef.current = false;
  });

  return [shouldSkip, skip];
}

export function formatBulkActions(bulkActionList: BulkAction[]) {
  if (!bulkActionList) return;
  const isExportAllExist = bulkActionList
    .join(",")
    .includes(exportBulkActionNames.join(","));
  const list: { name: string; type: string }[] = [];

  bulkActionList.forEach((action) => {
    if (isExportAllExist && action === exportBulkActionNames[1]) return;

    list.push({
      name: getBulkActionName(action),
      type:
        isExportAllExist && action === exportBulkActionNames[0]
          ? "export_dropdown"
          : action,
    });
  });

  return list;
}
